import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Zap, Loader2, Info } from "lucide-react";

export default function LastMinuteStats({ propId, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/last-minute-stats?lang=${lang}`);
      setD(data);
      const now = new Date();
      const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const has = (data.months || []).some((m) => m.key === curKey);
      setKey(has ? curKey : (data.months?.length ? data.months[data.months.length - 1].key : null));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [propId, lang]);
  useEffect(() => { load(); }, [load]);

  const Head = (
    <div className="flex items-center gap-2 mb-1"><Zap className="w-5 h-5 text-primary" />
      <h2 className="text-lg font-semibold">{L("Prenotazioni last-minute", "Last-minute bookings")}</h2></div>
  );

  if (loading && !d) return <div className="bg-card border border-border rounded-lg p-6"><Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" /></div>;
  if (!d) return null;

  if (!d.has_data || !d.has_booking_dates) {
    return (
      <div className="bg-card border border-border rounded-lg p-6" data-testid="last-minute-stats">
        {Head}
        <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground bg-amber-500/10 rounded-lg p-3" data-testid="lm-empty">
          <Info className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" />
          {!d.has_booking_dates
            ? L("Servono le prenotazioni con la data di inserimento (importa un file del PMS che includa la data di prenotazione).",
                "Bookings with their creation date are required (import a PMS file that includes the booking date).")
            : L("Nessuna prenotazione disponibile.", "No bookings available.")}
        </div>
      </div>
    );
  }

  const row = d.months.find((m) => m.key === key) || d.months[d.months.length - 1];
  const pct = row.total ? Math.round((row.last_minute / row.total) * 100) : 0;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6 space-y-4" data-testid="last-minute-stats">
      <div className="flex items-center justify-between flex-wrap gap-3">
        {Head}
        <select value={key || ""} onChange={(e) => setKey(e.target.value)} data-testid="lm-month"
          className="h-9 rounded-md border border-border bg-background px-2 text-sm">
          {d.months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>

      <p className="text-sm text-muted-foreground">
        {L("Prenotazioni arrivate lo stesso giorno, il giorno prima o max 2 giorni prima del check-in.",
           "Bookings that arrived the same day, the day before, or at most 2 days before check-in.")}
      </p>

      <div className="rounded-lg border border-border bg-muted/30 p-6 text-center" data-testid="lm-figure">
        <div className="flex items-end justify-center gap-2">
          <span className="text-5xl font-bold text-primary leading-none">{row.last_minute}</span>
          <span className="text-2xl text-muted-foreground mb-1">/ {row.total}</span>
        </div>
        <p className="mt-3 text-base">
          {L(`${row.last_minute} last-minute su ${row.total} prenotazioni`,
             `${row.last_minute} last-minute out of ${row.total} bookings`)}
          <span className="text-muted-foreground"> · {pct}%</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{row.label}</p>
      </div>
    </motion.div>
  );
}
